import type { AppConfiguration } from '../../../AppConfiguration';
import { mergeConfiguration } from './mergeConfiguration';

declare global {
    interface Window {
        /**
         * Production: written by the ReactRuntime host before the bundle runs (`Views/Home/Index.cshtml` puts
         * `<script src="/api/config/configuration.js">` ahead of the app's head content; `ConfigController` serves
         * appconfiguration.json merged with appconfiguration.<Environment>.json). Dev: filled by `ConfigurationProvider.init()`.
         */
        AppConfiguration: AppConfiguration;
    }
}

/** The host route that sets `window.AppConfiguration` (ReactRuntime `ConfigController`; `vite preview` serves it too). */
const HOSTED_CONFIGURATION_SCRIPT = '/api/config/configuration.js';

/** Path the Vite dev server proxies to `VITE_API_PROXY_TARGET` (see `vite.config.ts`). */
const DEV_API_PROXY_PATH = '/__ark-api';

export class ConfigurationProvider {

    static readonly HostedConfigurationScript = HOSTED_CONFIGURATION_SCRIPT;
    static readonly DevApiProxyPath = DEV_API_PROXY_PATH;

    /**
     * 1. `window.AppConfiguration` already set (the ReactRuntime host, or a test harness): used as is.
     * 2. `npm run dev`: the bundled `src/appconfiguration.json` + `appconfiguration.<Environment>.json`.
     * 3. A production bundle without it (`npm run preview`): loads the host's configuration script.
     */
    static async init() {
        if (window.AppConfiguration) return;
        window.AppConfiguration = import.meta.env.DEV ? await readBundled() : await readHosted();
    }

    static getConfiguration(): AppConfiguration {
        return window.AppConfiguration;
    }
}

/**
 * DEV ONLY (tree-shaken from builds): `appconfiguration.json` picks the environment (`"Environment": "local"`), whose
 * file is merged over it with the host's rules. `VITE_APP_ENVIRONMENT` picks another file without editing the
 * committed one (`local-qa`, `local-prod`).
 */
async function readBundled(): Promise<AppConfiguration> {
    const base = (await import('../../../appconfiguration.json')).default as unknown as AppConfiguration;
    const environment = import.meta.env.VITE_APP_ENVIRONMENT || base.Environment;
    const files = import.meta.glob<{ default: unknown }>('../../../appconfiguration.*.json');
    const loader = files[`../../../appconfiguration.${environment}.json`];
    if (!loader) throw new Error(`src/appconfiguration.${environment}.json does not exist.`);
    const config = mergeConfiguration(base, (await loader()).default);
    return applyDevEnvironment(config);
}

/**
 * DEV ONLY: `VITE_API_PROXY_TARGET` points `App.BackendUrl` at the dev-server proxy, so the local app (and a Playwright
 * run with an injected token, see `identity/DevToken.ts`) talks to a remote Ark.Core without a CORS entry.
 */
function applyDevEnvironment(config: AppConfiguration): AppConfiguration {
    const target = import.meta.env.VITE_API_PROXY_TARGET;
    if (!target) return config;
    return {
        ...config,
        App: { ...config.App, BackendUrl: `${window.location.origin}${DEV_API_PROXY_PATH}` },
    };
}

/** Production bundle served without the configuration script in its page: load the host route ourselves. */
async function readHosted(): Promise<AppConfiguration> {
    await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = HOSTED_CONFIGURATION_SCRIPT;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Unable to load ${HOSTED_CONFIGURATION_SCRIPT}.`));
        document.head.appendChild(script);
    });
    if (!window.AppConfiguration) {
        throw new Error(`${HOSTED_CONFIGURATION_SCRIPT} did not set window.AppConfiguration.`);
    }
    return window.AppConfiguration;
}
